import { useEffect } from "react";
import Lenis from "lenis";

export default function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.6,
    });
    window.__lenis = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return null;
}

const NAV_OFFSET = 70;
let activeCancel = null;

// Precise scroll that keeps re-aligning to the target's absolute position while
// the layout settles (lazy images / fonts), and yields immediately if the user
// takes over. No fixed timeout guessing — it stops once the target is stable.
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;

  if (activeCancel) activeCancel();

  let cancelled = false;
  const onUserScroll = () => cancel();
  const onKey = (e) => {
    if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"].includes(e.key)) cancel();
  };
  const cleanup = () => {
    window.removeEventListener("wheel", onUserScroll);
    window.removeEventListener("touchstart", onUserScroll);
    window.removeEventListener("keydown", onKey);
    if (activeCancel === cancel) activeCancel = null;
  };
  function cancel() {
    cancelled = true;
    cleanup();
  }
  activeCancel = cancel;

  window.addEventListener("wheel", onUserScroll, { passive: true });
  window.addEventListener("touchstart", onUserScroll, { passive: true });
  window.addEventListener("keydown", onKey);

  // Absolute document position of the target (invariant to current scroll).
  const targetTop = () =>
    Math.max(0, el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET);

  const goto = (top) => {
    if (window.__lenis) window.__lenis.scrollTo(top);
    else window.scrollTo({ top, behavior: "smooth" });
  };

  let last = targetTop();
  goto(last);

  const start = performance.now();
  let stableFrames = 0;
  const tick = () => {
    if (cancelled) return;
    const now = performance.now();
    const t = targetTop();
    if (Math.abs(t - last) > 1) {
      last = t;
      stableFrames = 0;
      goto(t); // layout shifted -> smoothly retarget
    } else {
      stableFrames += 1;
    }
    // Stop once the target has been stable for a few frames and the scroll has
    // reached it, or after a safety window.
    const reached = Math.abs(window.scrollY - last) < 2;
    if ((stableFrames >= 12 && reached) || now - start > 3000) {
      cleanup();
    } else {
      requestAnimationFrame(tick);
    }
  };
  requestAnimationFrame(tick);
}
